import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import styled from 'styled-components';
import { adminMatchesAPI } from '../services/adminApi';
import AdminScoreModal from './AdminScoreModal';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

const Container = styled.div`
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    gap: 0.75rem;
  }
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.65rem 0.85rem;
    font-size: 0.9rem;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #043663 0%, #032a4f 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 8px;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AccordionItem = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  background: ${props => props.isOpen ? '#f8fafc' : 'white'};
  transition: all 0.2s ease;
  border-bottom: ${props => props.isOpen ? '1px solid #e5e7eb' : 'none'};
  
  &:hover {
    background: #f8fafc;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const DateLabel = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
`;

const MatchCount = styled.span`
  padding: 0.25rem 0.75rem;
  background: #043663;
  color: white;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ChevronIcon = styled.span`
  font-size: 1.25rem;
  color: #6b7280;
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const AccordionContent = styled.div`
  padding: ${props => props.isOpen ? '1.5rem' : '0'};
  max-height: ${props => props.isOpen ? '10000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

const MatchesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MatchCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.25rem;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ClickableMatchCard = styled(MatchCard)`
  cursor: pointer;
  &:hover {
    border-color: #043663;
    background: #f0f9ff;
  }
`;

const MatchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }
`;

const MatchTeams = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
`;

const MatchDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DetailValue = styled.span`
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 500;
`;

// eslint-disable-next-line no-unused-vars
const ParsedDateBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
`;

// eslint-disable-next-line no-unused-vars
const OriginalText = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  font-style: italic;
  margin-top: 0.25rem;
`;

const LoadingContainer = styled.div`
  padding: 4rem;
  text-align: center;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  padding: 2rem;
  text-align: center;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 8px;
`;

const EmptyState = styled.div`
  padding: 4rem;
  text-align: center;
  color: #6b7280;
`;

// eslint-disable-next-line no-unused-vars
const NoDateGroup = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const AdminScheduledMatches = () => {
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedCanton, setSelectedCanton] = useState('');
  const [openDates, setOpenDates] = useState(new Set());
  const [scoreModalMatch, setScoreModalMatch] = useState(null);
  const queryClient = useQueryClient();

  const seasonPhase = useAdminSeasonPhaseFilter({
    allowAll: true,
    autoDefault: true,
  });
  const selectedSeason = seasonPhase.seasonId;

  // Fetch all scheduled matches (without canton filter) to get available cantons
  const { data: allMatchesData } = useQuery(
    ['scheduledMatchesAll', selectedSeason, selectedDivision],
    () => adminMatchesAPI.getScheduled({
      season_id: selectedSeason || undefined,
      division_id: selectedDivision || undefined
    }),
    {
      staleTime: 30000,
      cacheTime: 300000,
    }
  );

  // Fetch scheduled matches with filters
  const { data: matchesData, isLoading, error } = useQuery(
    ['scheduledMatches', selectedSeason, selectedDivision, selectedCanton],
    () => adminMatchesAPI.getScheduled({
      season_id: selectedSeason || undefined,
      division_id: selectedDivision || undefined,
      canton: selectedCanton || undefined
    }),
    {
      staleTime: 30000,
      cacheTime: 300000,
    }
  );

  const matches = matchesData?.data?.matches || [];
  const matchCount = matchesData?.data?.count || 0;

  // Get unique cantons from all matches (without canton filter) for filter dropdown
  const availableCantons = React.useMemo(() => {
    const allMatches = allMatchesData?.data?.matches || [];
    const cantonSet = new Set();
    allMatches.forEach(match => {
      if (match.canton) {
        cantonSet.add(match.canton);
      }
    });
    return Array.from(cantonSet).sort();
  }, [allMatchesData]);

  // Helper to validate date string (YYYY-MM-DD format)
  // Handles both string dates and Date objects from database
  const isValidDate = (dateInput) => {
    if (!dateInput) return false;
    
    let dateString = '';
    // Handle Date objects or ISO strings
    if (dateInput instanceof Date) {
      if (isNaN(dateInput.getTime())) return false;
      const year = dateInput.getFullYear();
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      dateString = `${year}-${month}-${day}`;
    } else if (typeof dateInput === 'string') {
      // Extract YYYY-MM-DD from ISO string if needed (e.g., "2025-11-28T00:00:00.000Z")
      const match = dateInput.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match) {
        dateString = match[1];
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        dateString = dateInput;
      } else {
        return false;
      }
    } else {
      return false;
    }
    
    // Check if it's a valid date
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;
    
    // Verify the date string matches the parsed date (prevents invalid dates like 2024-13-45)
    const [year, month, day] = dateString.split('-').map(Number);
    return date.getFullYear() === year && 
           date.getMonth() + 1 === month && 
           date.getDate() === day;
  };

  // Helper to get date object from string for sorting
  const getDateForSorting = (dateInput) => {
    if (!dateInput) return null;
    
    // Handle Date objects
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }
    
    // Handle strings - extract YYYY-MM-DD
    if (typeof dateInput === 'string') {
      const match = dateInput.match(/^(\d{4}-\d{2}-\d{2})/);
      const dateString = match ? match[1] : dateInput;
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'Not specified';
    if (!isValidDate(dateInput)) return 'Invalid date';
    try {
      let year, month, day;
      
      if (dateInput instanceof Date) {
        // Use local date components to avoid timezone shift
        year = dateInput.getFullYear();
        month = dateInput.getMonth();
        day = dateInput.getDate();
      } else if (typeof dateInput === 'string') {
        // Extract YYYY-MM-DD and parse components
        const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
          year = parseInt(match[1], 10);
          month = parseInt(match[2], 10) - 1; // month is 0-indexed
          day = parseInt(match[3], 10);
        } else {
          // Fallback to Date parsing
          const date = new Date(dateInput);
          year = date.getFullYear();
          month = date.getMonth();
          day = date.getDate();
        }
      } else {
        const date = new Date(dateInput);
        year = date.getFullYear();
        month = date.getMonth();
        day = date.getDate();
      }
      
      // Create date using local timezone and format it
      const date = new Date(year, month, day);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return String(dateInput);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Not specified';
    // Time should be in HH:MM format
    if (/^\d{2}:\d{2}$/.test(timeString)) {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    }
    return timeString;
  };

  const formatDateForGrouping = (dateInput) => {
    if (!isValidDate(dateInput)) return null;
    try {
      let year, month, day;
      
      if (dateInput instanceof Date) {
        // Use local date components to avoid timezone shift
        year = dateInput.getFullYear();
        month = dateInput.getMonth();
        day = dateInput.getDate();
      } else if (typeof dateInput === 'string') {
        // Extract YYYY-MM-DD and parse components
        const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
          year = parseInt(match[1], 10);
          month = parseInt(match[2], 10) - 1; // month is 0-indexed
          day = parseInt(match[3], 10);
        } else {
          // Fallback to Date parsing
          const date = new Date(dateInput);
          year = date.getFullYear();
          month = date.getMonth();
          day = date.getDate();
        }
      } else {
        const date = new Date(dateInput);
        year = date.getFullYear();
        month = date.getMonth();
        day = date.getDate();
      }
      
      // Create date using local timezone and format it
      const date = new Date(year, month, day);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return null;
    }
  };

  // Group matches by date - only use match_date (standard format)
  const groupMatchesByDate = () => {
    const grouped = {};
    const noDateMatches = [];

    matches.forEach(match => {
      // Only use match_date (standard format), filter out invalid dates
      if (match.match_date && isValidDate(match.match_date)) {
        // Convert to YYYY-MM-DD format for consistent key
        let dateKey = '';
        if (match.match_date instanceof Date) {
          const year = match.match_date.getFullYear();
          const month = String(match.match_date.getMonth() + 1).padStart(2, '0');
          const day = String(match.match_date.getDate()).padStart(2, '0');
          dateKey = `${year}-${month}-${day}`;
        } else if (typeof match.match_date === 'string') {
          const match_result = match.match_date.match(/^(\d{4}-\d{2}-\d{2})/);
          dateKey = match_result ? match_result[1] : match.match_date;
        } else {
          dateKey = String(match.match_date);
        }
        
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(match);
      } else {
        // No valid date - exclude from display
        noDateMatches.push(match);
      }
    });

    // Sort dates in ascending order (next coming date first)
    const sortedDates = Object.keys(grouped).sort((a, b) => {
      const dateA = getDateForSorting(a);
      const dateB = getDateForSorting(b);
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA - dateB;
    });

    // Sort matches within each date group by time (if available)
    sortedDates.forEach(dateKey => {
      grouped[dateKey].sort((a, b) => {
        const timeA = a.match_time || '';
        const timeB = b.match_time || '';
        if (!timeA && !timeB) return 0;
        if (!timeA) return 1;
        if (!timeB) return -1;
        return timeA.localeCompare(timeB);
      });
    });

    return { grouped, sortedDates, noDateMatches };
  };

  const toggleDate = (dateKey) => {
    const newOpenDates = new Set(openDates);
    if (newOpenDates.has(dateKey)) {
      newOpenDates.delete(dateKey);
    } else {
      newOpenDates.add(dateKey);
    }
    setOpenDates(newOpenDates);
  };

  const { grouped, sortedDates } = groupMatchesByDate();

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>Loading scheduled matches...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          Error loading scheduled matches: {error.message || 'Unknown error'}
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Scheduled Matches</Title>
        <Filters>
          <AdminSeasonPhaseFilters
            campaignId={seasonPhase.campaignId}
            seasonId={seasonPhase.seasonId}
            seasonOptions={seasonPhase.seasonOptions}
            phaseOptions={seasonPhase.phaseOptions}
            onCampaignChange={seasonPhase.onCampaignChange}
            onPhaseChange={seasonPhase.onPhaseChange}
            allowAll
            disabled={seasonPhase.isLoading}
          />
          <Select
            value={selectedCanton}
            onChange={(e) => setSelectedCanton(e.target.value)}
          >
            <option value="">All Cantons</option>
            {availableCantons.map((canton) => (
              <option key={canton} value={canton}>
                {canton}
              </option>
            ))}
          </Select>
          <Select
            value={selectedDivision}
            onChange={(e) => setSelectedDivision(e.target.value)}
          >
            <option value="">All Divisions</option>
            {/* Divisions would need to be fetched separately or included in the response */}
          </Select>
        </Filters>
      </Header>

      <StatsContainer>
        <StatCard>
          <StatValue>{matchCount}</StatValue>
          <StatLabel>Total Scheduled Matches</StatLabel>
        </StatCard>
      </StatsContainer>

      {matches.length === 0 ? (
        <EmptyState>
          <p>No scheduled matches found.</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Matches will appear here once teams submit match details (venue, date, etc.)
          </p>
        </EmptyState>
      ) : (
        <AccordionContainer>
          {/* Matches grouped by date */}
          {sortedDates.map((dateKey) => {
            const dateMatches = grouped[dateKey];
            const isOpen = openDates.has(dateKey);
            const displayDate = formatDateForGrouping(dateKey);
            
            return (
              <AccordionItem key={dateKey}>
                <AccordionHeader 
                  isOpen={isOpen}
                  onClick={() => toggleDate(dateKey)}
                >
                  <AccordionTitle>
                    <DateLabel>{displayDate}</DateLabel>
                    <MatchCount>{dateMatches.length} match{dateMatches.length !== 1 ? 'es' : ''}</MatchCount>
                  </AccordionTitle>
                  <ChevronIcon isOpen={isOpen}>▼</ChevronIcon>
                </AccordionHeader>
                
                <AccordionContent isOpen={isOpen}>
                  <MatchesList>
                    {dateMatches.map((match) => (
                      <ClickableMatchCard
                        key={match.id}
                        onClick={() => setScoreModalMatch(match)}
                      >
                        <MatchHeader>
                          <MatchTeams>
                            {match.team1_name} vs {match.team2_name}
                          </MatchTeams>
                          <div style={{ textAlign: 'right' }}>
                            <DetailLabel>Division</DetailLabel>
                            <DetailValue>{match.division_name || 'N/A'}</DetailValue>
                          </div>
                        </MatchHeader>

                        <MatchDetails>
                          <DetailItem>
                            <DetailLabel>Date</DetailLabel>
                            <DetailValue>{formatDate(match.match_date)}</DetailValue>
                          </DetailItem>

                          <DetailItem>
                            <DetailLabel>Time</DetailLabel>
                            <DetailValue>{formatTime(match.match_time)}</DetailValue>
                          </DetailItem>

                          <DetailItem>
                            <DetailLabel>Court/Venue</DetailLabel>
                            <DetailValue>{match.court_name || match.venue || 'TBD'}</DetailValue>
                          </DetailItem>

                          <DetailItem>
                            <DetailLabel>Location</DetailLabel>
                            <DetailValue>{match.court_location || 'Not specified'}</DetailValue>
                          </DetailItem>

                          {match.match_details && match.match_details.trim() && (
                            <DetailItem>
                              <DetailLabel>Additional Details (Free Text)</DetailLabel>
                              <DetailValue style={{ 
                                whiteSpace: 'pre-wrap', 
                                wordBreak: 'break-word',
                                maxWidth: '100%',
                                fontSize: '0.875rem',
                                color: '#4b5563'
                              }}>
                                {match.match_details}
                              </DetailValue>
                            </DetailItem>
                          )}

                          <DetailItem>
                            <DetailLabel>Match Window</DetailLabel>
                            <DetailValue>
                              {match.game_window_start
                                ? formatDate(match.game_window_start)
                                : 'Not specified'}
                              {match.game_window_end &&
                                ` - ${formatDate(match.game_window_end)}`}
                            </DetailValue>
                          </DetailItem>

                          <DetailItem>
                            <DetailLabel>Status</DetailLabel>
                            <DetailValue style={{
                              color: match.status === 'completed' ? '#059669' : 
                                     match.status === 'score_submitted' ? '#d97706' : '#6b7280'
                            }}>
                              {match.status || 'scheduled'}
                            </DetailValue>
                          </DetailItem>

                          <DetailItem>
                            <DetailLabel>Details Submitted</DetailLabel>
                            <DetailValue>
                              {match.details_submitted_at
                                ? formatDate(match.details_submitted_at)
                                : 'N/A'}
                            </DetailValue>
                          </DetailItem>
                        </MatchDetails>
                      </ClickableMatchCard>
                    ))}
                  </MatchesList>
                </AccordionContent>
              </AccordionItem>
            );
          })}

        </AccordionContainer>
      )}

      <AdminScoreModal
        isOpen={!!scoreModalMatch}
        onClose={() => setScoreModalMatch(null)}
        match={scoreModalMatch}
        onSuccess={() => {
          queryClient.invalidateQueries(['scheduledMatches']);
          queryClient.invalidateQueries(['scheduledMatchesAll']);
        }}
      />
    </Container>
  );
};

export default AdminScheduledMatches;

